// shaders and geometry modification lifted from: https://github.com/mrdoob/three.js/blob/master/examples/webgl_materials_wireframe.html

import { useRef, VoidFunctionComponent } from 'react';
import { useFrame, useLoader } from '@react-three/fiber';
import { AdditiveBlending, BufferGeometryLoader, DoubleSide, Group, MathUtils, ShaderMaterial } from 'three';
import { buildPublicUrl } from '@/lib/globals';
import { useLerpedMouse } from '@/hooks/useLerpedMouse';
import { useDeviceOrientation } from '@/hooks/useDeviceOrientation';

export const helixURL = buildPublicUrl(`3d/helix.json`);

const vertexShader = `
attribute vec3 center;
varying vec3 vCenter;

void main() {
    vCenter = center;
    gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
}`;

const fragmentShader = `
uniform float thickness;
varying vec3 vCenter;

void main() {
    vec3 afwidth = fwidth( vCenter.xyz );
    vec3 edge3 = smoothstep( ( thickness - 1.0 ) * afwidth, thickness * afwidth, vCenter.xyz );
    float edge = 1.0 - min( min( edge3.x, edge3.y ), edge3.z );
    gl_FragColor.rgb = gl_FrontFacing ? vec3( 0.2, 0.2, 0.23 ) : vec3( 0.1, 0.1, 0.1 );
    gl_FragColor.a = gl_FrontFacing ? edge * 0.5 : 0.0;
}`;

const material = new ShaderMaterial({
    uniforms: { thickness: { value: 2 } },
    vertexShader,
    fragmentShader,
    side: DoubleSide,
    blending: AdditiveBlending,
    alphaToCoverage: false, // only works when WebGLRenderer's "antialias" is set to "true"
});
material.extensions.derivatives = true;

export const Helix: VoidFunctionComponent = ({ ...props }: JSX.IntrinsicElements['group']) => {
    const rotationGroup = useRef<Group>(new Group());
    const positionGroup = useRef<Group>(new Group());
    const geometry = useLoader(BufferGeometryLoader, helixURL);
    const mouse = useLerpedMouse();
    const { orientation } = useDeviceOrientation();

    useFrame(({ clock }) => {
        if (rotationGroup.current) {
            rotationGroup.current.rotation.set(0, (Math.PI * 2 * clock.elapsedTime) / 10, 0);
        }

        if (positionGroup.current) {
            if (orientation.current && orientation.current.beta !== null && orientation.current.gamma !== null) {
                positionGroup.current.position.set(MathUtils.lerp(0, 3, orientation.current.gamma / 180), 0, 0);
            } else {
                positionGroup.current.position.set(MathUtils.lerp(0, 1.0, mouse.current.x), 0, 0);
            }
        }
    });

    return (
        <group ref={positionGroup}>
            <group position={[0, -10, -3]} ref={rotationGroup} {...props}>
                <mesh>
                    <bufferGeometry attach="geometry" {...geometry} />
                    <shaderMaterial attach="material" {...material} />
                </mesh>
                {/* <mesh scale={[0.99, 1, 0.99]}>
                    <bufferGeometry attach="geometry" {...geometry} />
                    <meshBasicMaterial color={"#272727"} />
                </mesh> */}
            </group>
        </group>
    );
};
